/**
 * Agent Builder - Visual editor for creating/editing agent configurations
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  AgentConfig,
  ModelProvider,
  ModelConfig,
  PromptTemplate,
  ContextField,
  ContextFieldType,
  OutputSchema,
  OutputFormat,
  ModelProviderInfo,
  ModelInfo,
  ContextFieldTypeInfo,
  OutputFormatInfo,
} from '../types/agent';
import type { TaskTypeInfo } from '../types/pipeline';
import { apiClient, slugifyAgentName } from '../api/client';
import { dedupeSlug } from '../utils/agentExport';
import { DEFAULT_AGENT_TEMPERATURE, DEFAULT_OUTPUT_SCHEMA_TEXT } from '../utils/constants';
import { PromptEditor } from './PromptEditor';
import { AgentRunPanel } from './AgentRunPanel';
import type { ConnectedInputInfo, UpstreamStep } from '../utils/outputPath';
import { VariableConfigPopover, contextFieldSources, describeSelection, parseSelection } from './VariableConfigPopover';
import { ArrowLeftIcon, CheckIcon, PlayIcon, PlusIcon } from './icons';
import { TagInput } from './TagInput';
import './AgentBuilder.css';

interface AgentBuilderProps {
  agentId?: string; // If provided, load and edit existing agent
  onSave?: (agent: AgentConfig) => void;
  // Fired after an in-place update (existing agent) succeeds, so callers can
  // refresh any cached list/card view without navigating away from the form.
  onUpdate?: (agent: AgentConfig) => void;
  onCancel?: () => void;
  initialTab?: AgentBuilderTab;
  // Prefills a new (unsaved) agent with this config - used to duplicate an existing agent.
  templateAgent?: AgentConfig;
  // Slugs already taken, so a new agent's name-derived slug can be de-duplicated on create.
  existingSlugs?: Set<string>;
  // Steps feeding the workflow node this agent was opened from - lets a "Previous step"
  // variable pick a specific field / list item of one of their outputs.
  upstreamSteps?: UpstreamStep[];
  // Data sources wired to that node - what Single item / All items / Tracker schema variables read.
  connectedInputs?: ConnectedInputInfo[];
  // Categories / tags already used by other agents - offered as suggestions.
  knownCategories?: string[];
  knownTags?: string[];
}

export type AgentBuilderTab = 'basic' | 'model' | 'prompt' | 'output' | 'sandbox';

export const AgentBuilder: React.FC<AgentBuilderProps> = ({
  agentId,
  onSave,
  onUpdate,
  onCancel,
  initialTab,
  templateAgent,
  existingSlugs,
  upstreamSteps,
  connectedInputs,
  knownCategories = [],
  knownTags = [],
}) => {
  const [agent, setAgent] = useState<Partial<AgentConfig>>({
    name: '',
    description: '',
    version: '1.0.0',
    model: {
      provider: ModelProvider.RAIQON,
      model_name: 'raven_3_medium',
      temperature: DEFAULT_AGENT_TEMPERATURE,
      top_p: 1.0,
      stop_sequences: [],
      repetition_penalty: 1.0,
    },
    prompt: {
      system_prompt: '',
      user_prompt_template: '',
      variables: [],
      examples: [],
    },
    context_fields: [],
    output: {
      format: OutputFormat.JSON,
      validation_rules: [],
    },
    timeout: 300,
    retry_count: 3,
    tags: [],
    is_public: false,
  });

  const [providers, setProviders] = useState<ModelProviderInfo[]>([]);
  const [availableModels, setAvailableModels] = useState<ModelInfo[]>([]);
  const [contextFieldCategories, setContextFieldCategories] = useState<ContextFieldTypeInfo[]>([]);
  const [outputFormats, setOutputFormats] = useState<OutputFormatInfo[]>([]);
  // Raw textarea contents for the JSON Schema editor, kept separate from
  // agent.output.output_schema so partially-typed (invalid) JSON doesn't get
  // discarded on every keystroke.
  const [schemaText, setSchemaText] = useState<string>('');
  const [taskTypes, setTaskTypes] = useState<TaskTypeInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AgentBuilderTab>(initialTab ?? 'basic');
  const [saveSuccess, setSaveSuccess] = useState(false);
  // The variable whose data-source popover is open, anchored at the clicked token / list row.
  const [openVariable, setOpenVariable] = useState<{ name: string; anchor: DOMRect } | null>(null);

  // Tracks the provider whose models we most recently requested. Async responses
  // that arrive after the user (or loadAgent) has moved on are dropped so a stale
  // /models/all response can't overwrite the current list.
  const activeProviderRequest = useRef<ModelProvider | null>(null);

  // Load agent if editing
  useEffect(() => {
    if (agentId) {
      loadAgent(agentId);
    }
  }, [agentId]);

  // Seed a duplicated agent's full config. Identity fields are dropped so it saves as a new agent.
  useEffect(() => {
    if (agentId || !templateAgent) return;
    const {
      id: _id,
      slug: _slug,
      created_at: _createdAt,
      updated_at: _updatedAt,
      created_by: _createdBy,
      usage_count: _usageCount,
      ...rest
    } = structuredClone(templateAgent);
    setAgent({ ...rest, name: `${templateAgent.name} (copy)`, version: '1', is_default: false });
    setSchemaText(rest.output?.output_schema ? JSON.stringify(rest.output.output_schema, null, 2) : '');
    if (rest.model?.provider) {
      loadModelsForProvider(rest.model.provider, rest.model.model_name);
    }
    // Seeded once per template; later edits are the user's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateAgent]);

  // Load metadata (providers, models, etc.)
  useEffect(() => {
    loadMetadata();
  }, []);

  // Load available models when provider changes. When editing an existing
  // agent, hold off firing this effect against the seeded placeholder model
  // until loadAgent has actually resolved for the current agentId - loadAgent
  // makes its own explicit loadModelsForProvider call with the real model
  // name once it loads, and firing here first would race with that call (see
  // activeProviderRequest guard in loadModelsForProvider).
  //
  // This can't be a ref that's merely "true on first invocation": React 18
  // StrictMode double-invokes mount effects in dev, which consumes a
  // one-shot guard on the first synthetic invocation and lets the second
  // slip through while `agent` still holds the seeded placeholder - firing
  // loadModelsForProvider with no currentModelName, whose stale-model
  // fallback then matches the placeholder's base_model and overwrites the
  // real selection loadAgent just set (that endpoint's mesh availability
  // refresh makes it resolve after loadAgent's own call, so "last write"
  // always wins). Keying the skip on the agentId that's actually finished
  // loading is safe no matter how many times the effect fires before that.
  const loadedAgentIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (agentId && loadedAgentIdRef.current !== agentId) {
      return;
    }
    if (agent.model?.provider) {
      loadModelsForProvider(agent.model.provider);
    }
  }, [agent.model?.provider]);

  const loadAgent = async (id: string) => {
    try {
      setLoading(true);
      const loadedAgent = await apiClient.getAgent(id);
      setAgent(loadedAgent);
      setSchemaText(loadedAgent.output?.output_schema ? JSON.stringify(loadedAgent.output.output_schema, null, 2) : '');
      // Now that real agent data has replaced the seeded placeholder, the
      // provider-change effect above is safe to act on for this agentId.
      loadedAgentIdRef.current = id;
      // Explicitly (re)load models for the loaded agent's provider - the
      // provider-change effect above won't fire when the loaded provider is
      // the same as the seeded placeholder default (or the same as a
      // previously-loaded agent/version), leaving availableModels empty and
      // the Model dropdown unable to show the agent's actual selection.
      if (loadedAgent.model?.provider) {
        loadModelsForProvider(loadedAgent.model.provider, loadedAgent.model.model_name);
      }
    } catch (err) {
      setError(`Failed to load agent: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const loadMetadata = async () => {
    console.log('Loading metadata...');

    // Load each piece independently - don't fail everything if one fails
    try {
      console.log('Loading providers...');
      const providersData = await apiClient.getModelProviders();
      console.log('Providers loaded:', providersData);
      setProviders(providersData);
    } catch (err: any) {
      console.error('Failed to load providers:', err);
      console.error('Error details:', err.response?.data);
    }

    try {
      console.log('Loading context field types...');
      const typesData = await apiClient.getContextFieldTypes();
      console.log('Context types loaded:', typesData);
      setContextFieldCategories(typesData.categories || []);
    } catch (err: any) {
      console.error('Failed to load context field types:', err);
      console.error('Error details:', err.response?.data);
      // Fallback to basic categories
      console.log('Using fallback context field categories');
      setContextFieldCategories([
        {
          id: 'item',
          name: 'Item Fields',
          description: 'Current item fields',
          fields: [{ id: 'item.id', name: 'Item ID', description: 'Item identifier' }]
        },
      ]);
    }

    try {
      console.log('Loading output formats...');
      const formatsData = await apiClient.getOutputFormats();
      console.log('Output formats loaded:', formatsData);
      setOutputFormats(formatsData);
    } catch (err: any) {
      console.error('Failed to load output formats:', err);
      console.error('Error details:', err.response?.data);
      // Fallback to hardcoded formats
      console.log('Using fallback output formats');
      setOutputFormats([
        { id: 'text', name: 'Plain Text', description: 'Unstructured text output' },
        { id: 'json', name: 'JSON', description: 'Free-form JSON output' },
        { id: 'structured', name: 'Structured', description: 'JSON with enforced schema' },
      ]);
    }

    try {
      console.log('Loading task types...');
      const taskTypesData = await apiClient.getTaskTypes();
      console.log('Task types loaded:', taskTypesData);
      setTaskTypes(taskTypesData);
    } catch (err: any) {
      console.error('Failed to load task types:', err);
      console.error('Error details:', err.response?.data);
      setTaskTypes([]);
    }
  };

  // currentModelName lets a caller that just set agent state synchronously
  // (loadAgent) pass the up-to-date model name explicitly, since this
  // function's own `agent` closure won't reflect a setAgent call made
  // earlier in the same tick (state updates aren't synchronous).
  const loadModelsForProvider = async (provider: ModelProvider, currentModelName?: string) => {
    activeProviderRequest.current = provider;
    try {
      let models: ModelInfo[] = [];
      if (provider === ModelProvider.RAIQON) {
        models = await apiClient.getRaiqonModels();
      } else if (provider === ModelProvider.AZURE_AI_FOUNDRY) {
        models = await apiClient.getAzureAIFoundryModels();
      }
      // Ignore responses that were superseded by a newer provider selection —
      // otherwise a slow RAIQON response can overwrite the Azure list loaded
      // moments later after loadAgent set the real provider.
      if (activeProviderRequest.current === provider) {
        setAvailableModels(models);
        const modelName = currentModelName ?? agent.model?.model_name;
        // model_name is only ever wired to a real ModelInfo.id by this
        // dropdown's own onChange - the seeded default (and any stale
        // model_name left over from switching providers) is a base_model
        // slug string, not an id, and model_slug never gets set alongside
        // it. Re-point at a real, savable model once the list we can
        // actually validate against has loaded.
        if (models.length > 0 && !models.some(m => m.id === modelName)) {
          const defaultModel = models.find(m => m.base_model === modelName) ?? models[0];
          updateModel({ model_name: defaultModel.id, model_slug: defaultModel.slug });
        }
      }
    } catch (err) {
      console.error('Failed to load models:', err);
    }
  };

  const handleSave = async () => {
    if (agentId && agent.is_default) return;
    try {
      setLoading(true);
      setError(null); // Clear any previous errors when saving

      // Validate required fields
      if (!agent.name || !agent.description) {
        setError('Name and description are required');
        return;
      }
      if (!agent.task_type) {
        setError('Task type is required');
        return;
      }

      console.log('Saving agent with model config:', agent.model);

      const savedAgent = agentId
        ? await apiClient.updateAgent(agentId, agent as AgentConfig)
        : await apiClient.createAgent({
            ...agent,
            slug: dedupeSlug(slugifyAgentName(agent.name || ''), existingSlugs ?? new Set()),
          } as AgentConfig);

      console.log('Saved agent:', savedAgent);

      if (agentId) {
        // Stay on the edit page and show a success state instead of
        // navigating back via onSave (which AgentLibrary uses to return
        // to the library view). Don't overwrite local state with
        // savedAgent: the update/create endpoints return the backend's
        // flat AgentDetail shape (model_id/model_slug/config), not the
        // nested AgentConfig shape (model/prompt/output/context_fields)
        // this form uses, so doing so would blank out the model dropdown
        // and other fields. Local `agent` state already reflects what was
        // just saved.
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
        if (onUpdate) {
          onUpdate(savedAgent);
        }
      } else if (onSave) {
        onSave(savedAgent);
      }
    } catch (err) {
      setError(`Failed to save agent: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const updateModel = (updates: Partial<ModelConfig>) => {
    setAgent(prev => ({
      ...prev,
      model: { ...prev.model!, ...updates },
    }));
  };

  const updatePrompt = (updates: Partial<PromptTemplate>) => {
    setAgent(prev => ({
      ...prev,
      prompt: { ...prev.prompt!, ...updates },
    }));
  };

  const updateOutput = (updates: Partial<OutputSchema>) => {
    setAgent(prev => ({
      ...prev,
      output: { ...prev.output!, ...updates },
    }));
  };

  // For a variable used only outside the user prompt (e.g. in the system prompt), which the
  // template auto-detection below can't discover by itself.
  const addContextField = (anchor: DOMRect) => {
    const existing = new Set((agent.context_fields || []).map(field => field.name));
    let index = existing.size;
    while (existing.has(`variable_${index}`)) index++;
    const name = `variable_${index}`;
    setAgent(prev => ({
      ...prev,
      context_fields: [
        ...(prev.context_fields || []),
        { name, field_type: ContextFieldType.TEXT, description: '', required: true },
      ],
    }));
    setOpenVariable({ name, anchor });
  };

  const applyVariableConfig = (oldName: string, updates: Partial<ContextField> & { name: string }) => {
    const renamed = updates.name !== oldName;
    const renameIn = (text: string | undefined) => (text ?? '').split(`{${oldName}}`).join(`{${updates.name}}`);
    setAgent(prev => ({
      ...prev,
      // Renaming a variable renames its {placeholder} in both prompts too, so the two never drift apart.
      prompt: renamed
        ? {
            ...prev.prompt!,
            system_prompt: renameIn(prev.prompt?.system_prompt),
            user_prompt_template: renameIn(prev.prompt?.user_prompt_template),
            variables: (prev.prompt?.variables || []).map(v => (v === oldName ? updates.name : v)),
          }
        : prev.prompt,
      context_fields: (prev.context_fields || []).map(field =>
        field.name === oldName ? { ...field, ...updates, sources: updates.sources } : field
      ),
    }));
    setOpenVariable(null);
  };

  // A variable still used in the prompt is only reset to unconfigured (deleting it would just
  // re-stub it on the next keystroke); one that isn't is deleted outright.
  const removeVariable = (name: string) => {
    const inTemplate = (agent.prompt?.variables || []).includes(name);
    setAgent(prev => ({
      ...prev,
      context_fields: inTemplate
        ? (prev.context_fields || []).map(field =>
            field.name === name
              ? { name, field_type: ContextFieldType.TEXT, description: field.description, required: field.required }
              : field
          )
        : (prev.context_fields || []).filter(field => field.name !== name),
    }));
    setOpenVariable(null);
  };

  const extractVariables = (template: string) => {
    const variables = Array.from(new Set(Array.from(template.matchAll(/\{(\w+)\}/g)).map(match => match[1])));
    const variableSet = new Set(variables);

    // A template variable with no matching context field renders as the literal
    // "{name}" placeholder (the sandbox fails soft rather than erroring) - auto-stub
    // a field for any newly-typed variable so it's never silently left unresolved.
    const existingNames = new Set((agent.context_fields || []).map(field => field.name));
    const missingFields: ContextField[] = variables
      .filter(name => !existingNames.has(name))
      .map(name => ({
        name,
        field_type: ContextFieldType.TEXT,
        description: '',
        required: true,
      }));

    // Symmetrically, drop a field once its variable disappears from the template.
    // Only fields that were previously a recognized variable are removed, so
    // manually added fields (e.g. scope-sourced ones) are left untouched.
    const removedNames = new Set((agent.prompt?.variables || []).filter(name => !variableSet.has(name)));
    const survivingFields = removedNames.size
      ? (agent.context_fields || []).filter(field => !removedNames.has(field.name))
      : agent.context_fields || [];

    setAgent(prev => ({
      ...prev,
      prompt: { ...prev.prompt!, user_prompt_template: template, variables },
      context_fields: missingFields.length ? [...survivingFields, ...missingFields] : survivingFields,
    }));
  };

  const contextFields = agent.context_fields || [];
  const configuredNames = new Set(contextFields.filter(f => contextFieldSources(f).length > 0).map(f => f.name));
  const unconfiguredCount = contextFields.length - configuredNames.size;
  const openField = openVariable ? contextFields.find(field => field.name === openVariable.name) : undefined;

  if (loading && !agent.name) {
    return <div className="agent-builder loading">Loading agent...</div>;
  }

  return (
    <div className="agent-builder">
      <div className="agent-builder-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {onCancel && (
              <button
                onClick={onCancel}
                className="back-button"
                style={{
                  background: 'none',
                  border: '2px solid var(--border-color)',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                title="Back to agent list"
              >
                <ArrowLeftIcon /> Back
              </button>
            )}
            <h2 style={{ margin: 0 }}>{agentId ? 'Edit Agent' : templateAgent ? 'Duplicate Agent' : 'Create New Agent'}</h2>
          </div>
          <div className="agent-builder-actions">
            <button onClick={onCancel} className="btn-secondary" disabled={loading}>
              Cancel
            </button>
            <button
              onClick={handleSave}
              className={`btn-primary ${saveSuccess ? 'btn-success' : ''}`}
              disabled={loading || (!!agentId && agent.is_default)}
              title={agentId && agent.is_default ? 'Default agents cannot be edited' : undefined}
            >
              {loading ? 'Saving...' : saveSuccess ? <><CheckIcon /> Saved</> : (agentId ? 'Update Agent' : 'Create Agent')}
            </button>
          </div>
        </div>
        {error && !error.includes('metadata') && <div className="error-message">{error}</div>}
        {agentId && agent.is_default && (
          <div style={{
            padding: 'var(--spacing-md)',
            background: '#f0f9ff',
            border: '1px solid #0ea5e9',
            borderRadius: '6px',
            marginTop: 'var(--spacing-md)',
            fontSize: '0.875rem'
          }}>
            <strong style={{ color: '#0369a1' }}>Built-in default agent:</strong> its prompt and
            context are defined in code, so it can't be edited here. You can still test it in the Sandbox.
          </div>
        )}
      </div>

      <div className="agent-builder-tabs">
        <button
          className={activeTab === 'basic' ? 'active' : ''}
          onClick={() => setActiveTab('basic')}
        >
          Basic Info
        </button>
        <button
          className={activeTab === 'model' ? 'active' : ''}
          onClick={() => setActiveTab('model')}
        >
          Model
        </button>
        <button
          className={activeTab === 'prompt' ? 'active' : ''}
          onClick={() => setActiveTab('prompt')}
        >
          Prompt & Context
        </button>
        <button
          className={activeTab === 'output' ? 'active' : ''}
          onClick={() => setActiveTab('output')}
        >
          Output
        </button>
        <button
          className={`tab-sandbox ${activeTab === 'sandbox' ? 'active' : ''}`}
          onClick={() => setActiveTab('sandbox')}
        >
          <PlayIcon /> Sandbox
        </button>
      </div>

      <div className="agent-builder-content">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="tab-panel">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={agent.name || ''}
                onChange={(e) => setAgent({ ...agent, name: e.target.value })}
                placeholder="e.g., Requirement Classifier"
              />
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={agent.description || ''}
                onChange={(e) => setAgent({ ...agent, description: e.target.value })}
                placeholder="What does this agent do?"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Task Type *</label>
              <select
                value={agent.task_type || ''}
                onChange={(e) => setAgent({ ...agent, task_type: e.target.value })}
                disabled={!!agentId}
                title={agentId ? 'Task type cannot be changed after creation' : ''}
              >
                <option value="">
                  {taskTypes.length === 0 ? 'Loading task types...' : 'Select a task type...'}
                </option>
                {taskTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              <small>
                {agentId
                  ? 'Task type is fixed once an agent is created.'
                  : 'What kind of task this agent handles. Required.'}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="agent-category">Category</label>
              <input
                id="agent-category"
                type="text"
                list="agent-category-suggestions"
                value={agent.category || ''}
                onChange={(e) => setAgent({ ...agent, category: e.target.value })}
                placeholder="e.g., Analysis, Testing"
              />
              <datalist id="agent-category-suggestions">
                {knownCategories.map(c => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              {knownCategories.length > 0 && (
                <div className="category-suggestions">
                  {knownCategories.map(c => (
                    <button
                      key={c}
                      type="button"
                      className={`category-suggestion ${agent.category === c ? 'is-active' : ''}`}
                      onClick={() => setAgent({ ...agent, category: agent.category === c ? '' : c })}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="agent-tags">Tags</label>
              <TagInput
                id="agent-tags"
                value={agent.tags || []}
                onChange={(tags) => setAgent({ ...agent, tags })}
                suggestions={knownTags}
                placeholder="Type a tag and press Enter"
              />
              <small>Press Enter or comma to add a tag; Backspace removes the last one.</small>
            </div>

            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={agent.is_public || false}
                  onChange={(e) => setAgent({ ...agent, is_public: e.target.checked })}
                />
                Make public (visible to all users)
              </label>
            </div>
          </div>
        )}

        {/* Model Tab */}
        {activeTab === 'model' && (
          <div className="tab-panel">
            {agent.is_default && (
              <div style={{
                padding: 'var(--spacing-md)',
                background: '#f0f9ff',
                border: '1px solid #0ea5e9',
                borderRadius: '6px',
                marginBottom: 'var(--spacing-lg)',
                fontSize: '0.875rem'
              }}>
                <strong style={{ color: '#0369a1' }}>Built-in default agent:</strong> its model
                is fixed by the task type's server configuration, not this form, so model
                selection is disabled here.
              </div>
            )}

            <div className="form-group">
              <label>Provider</label>
              <select
                value={agent.model?.provider || ModelProvider.RAIQON}
                onChange={(e) => updateModel({ provider: e.target.value as ModelProvider })}
                disabled={agent.is_default}
              >
                {providers.length === 0 && <option value="">Loading providers...</option>}
                {providers.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Model</label>
              <select
                value={agent.model?.model_name || ''}
                onChange={(e) => {
                  const selected = availableModels.find(m => m.id === e.target.value);
                  updateModel({ model_name: e.target.value, model_slug: selected?.slug });
                }}
                disabled={agent.is_default}
              >
                {availableModels.map(m => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
              {availableModels.find(m => m.id === agent.model?.model_name)?.description && (
                <small>{availableModels.find(m => m.id === agent.model?.model_name)?.description}</small>
              )}
            </div>

            {availableModels.find(m => m.id === agent.model?.model_name)?.supports_reasoning_toggle && (
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={agent.model?.reasoning_enabled ?? true}
                    onChange={(e) => updateModel({ reasoning_enabled: e.target.checked })}
                  />
                  Enable reasoning (thinking)
                </label>
                <small>This model can "think" before answering. Disable to skip straight to the response.</small>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Temperature ({agent.model?.temperature ?? DEFAULT_AGENT_TEMPERATURE})</label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={agent.model?.temperature ?? DEFAULT_AGENT_TEMPERATURE}
                  onChange={(e) => updateModel({ temperature: parseFloat(e.target.value) })}
                />
                <small>Lower = more deterministic, Higher = more creative</small>
              </div>

              <div className="form-group">
                <label>Top P ({agent.model?.top_p || 1.0})</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={agent.model?.top_p || 1.0}
                  onChange={(e) => updateModel({ top_p: parseFloat(e.target.value) })}
                />
              </div>
            </div>

            {availableModels.find(m => m.id === agent.model?.model_name)?.supports_repetition_penalty && (
              <div className="form-group">
                <label>Repetition Penalty ({agent.model?.repetition_penalty || 1.0})</label>
                <input
                  type="range"
                  min="1"
                  max="2"
                  step="0.1"
                  value={agent.model?.repetition_penalty || 1.0}
                  onChange={(e) => updateModel({ repetition_penalty: parseFloat(e.target.value) })}
                />
                <small>Higher = less repetitive output. Only supported by raiqon-hosted models.</small>
              </div>
            )}

            <div className="form-group">
              <label>Max Tokens (optional)</label>
              <input
                type="number"
                value={agent.model?.max_tokens || ''}
                onChange={(e) => updateModel({ max_tokens: e.target.value ? parseInt(e.target.value) : undefined })}
                placeholder="Leave empty for model default"
              />
            </div>
          </div>
        )}

        {/* Prompt & Context Tab */}
        {activeTab === 'prompt' && (
          <div className="tab-panel">
            <div className="form-group">
              <label>System Prompt</label>
              <textarea
                value={agent.prompt?.system_prompt || ''}
                onChange={(e) => updatePrompt({ system_prompt: e.target.value })}
                placeholder="Define the agent's role and behavior..."
                rows={6}
                className="monospace"
              />
            </div>

            <div className="form-group">
              <label>User Prompt Template</label>
              <PromptEditor
                value={agent.prompt?.user_prompt_template || ''}
                onChange={extractVariables}
                recognizedNames={new Set(contextFields.map(field => field.name))}
                configuredNames={configuredNames}
                onVariableClick={(name, anchor) => setOpenVariable({ name, anchor })}
                placeholder="Use {variable_name} for placeholders..."
              />
              <small>
                Type {`{variable_name}`} to add a variable, then click it to choose where its data comes from.
              </small>
            </div>

            <div className="variables-panel">
              <div className="variables-panel__header">
                <span className="variables-panel__title">
                  Variables
                  {unconfiguredCount > 0 && (
                    <span className="variables-panel__badge">{unconfiguredCount} to configure</span>
                  )}
                </span>
                <button
                  className="variables-panel__add"
                  onClick={(e) => addContextField(e.currentTarget.getBoundingClientRect())}
                  title="Add a variable used outside the user prompt (e.g. in the system prompt)"
                >
                  <PlusIcon /> Add
                </button>
              </div>

              {contextFields.length === 0 ? (
                <div className="variables-panel__empty">
                  No variables yet - type <code>{'{summary}'}</code> in the prompt above to add one.
                </div>
              ) : (
                <ul className="variables-panel__list">
                  {contextFields.map(field => {
                    const summary = describeSelection(parseSelection(field), contextFieldCategories);
                    return (
                      <li key={field.name}>
                        <button
                          className={`variable-row ${summary ? '' : 'variable-row--unconfigured'} ${
                            openVariable?.name === field.name ? 'is-open' : ''
                          }`}
                          onClick={(e) => setOpenVariable({ name: field.name, anchor: e.currentTarget.getBoundingClientRect() })}
                        >
                          <code className="variable-row__name">{`{${field.name}}`}</code>
                          <span className="variable-row__source">{summary ?? 'Choose a data source'}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {openVariable && openField && (
              <VariableConfigPopover
                key={openVariable.name}
                field={openField}
                categories={contextFieldCategories}
                upstreamSteps={upstreamSteps}
                connectedInputs={connectedInputs}
                anchor={openVariable.anchor}
                takenNames={new Set(contextFields.map(field => field.name))}
                onApply={(updates) => applyVariableConfig(openVariable.name, updates)}
                onRemove={() => removeVariable(openVariable.name)}
                onClose={() => setOpenVariable(null)}
              />
            )}
          </div>
        )}

        {/* Sandbox Tab - kept mounted while hidden so its inputs and last result survive tab switches */}
        <div className="tab-panel tab-panel--wide" hidden={activeTab !== 'sandbox'}>
          <AgentRunPanel agent={agent} onEditPrompt={() => setActiveTab('prompt')} />
        </div>

        {/* Output Tab */}
        {activeTab === 'output' && (
          <div className="tab-panel">
            <div style={{
              padding: 'var(--spacing-md)',
              background: '#f0f9ff',
              border: '1px solid #0ea5e9',
              borderRadius: '6px',
              marginBottom: 'var(--spacing-lg)',
              fontSize: '0.875rem'
            }}>
              <strong style={{ color: '#0369a1' }}>Output Formats Explained:</strong>
              <ul style={{ margin: 'var(--spacing-xs) 0 0 0', paddingLeft: 'var(--spacing-lg)' }}>
                <li><strong>Plain Text</strong>: Agent returns unstructured text (narrative, explanation, etc.)</li>
                <li><strong>JSON</strong>: Agent returns JSON, but structure is not enforced</li>
                <li><strong>Structured</strong>: Agent MUST return JSON matching your schema (most reliable for automation)</li>
              </ul>
            </div>

            <div className="form-group">
              <label>Output Format</label>
              <select
                value={agent.output?.format || OutputFormat.JSON}
                onChange={(e) => updateOutput({ format: e.target.value as OutputFormat })}
              >
                {outputFormats.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              <small>
                {outputFormats.find(f => f.id === agent.output?.format)?.description}
              </small>
            </div>

            {agent.output?.format === OutputFormat.STRUCTURED && (
              <div className="form-group">
                <label>JSON Schema</label>
                <small style={{ display: 'block', marginBottom: 'var(--spacing-sm)' }}>
                  Define the structure of the agent's output. The model will be forced to return JSON matching this schema.
                  For models without native schema enforcement, this schema is automatically added to the system prompt as
                  an instruction — you don't need to (and shouldn't) describe the output format again yourself.
                </small>
                <textarea
                  value={schemaText}
                  onChange={(e) => {
                    const text = e.target.value;
                    setSchemaText(text);
                    try {
                      const output_schema = JSON.parse(text);
                      updateOutput({ output_schema });
                    } catch (err) {
                      // Invalid/incomplete JSON while typing - keep the raw text,
                      // don't touch agent.output.output_schema until it parses again.
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab' && schemaText.trim() === '') {
                      e.preventDefault();
                      setSchemaText(DEFAULT_OUTPUT_SCHEMA_TEXT);
                      updateOutput({ output_schema: JSON.parse(DEFAULT_OUTPUT_SCHEMA_TEXT) });
                    }
                  }}
                  placeholder={DEFAULT_OUTPUT_SCHEMA_TEXT}
                  rows={12}
                  className="monospace"
                />
                <small>Example: Force the agent to return classification, confidence, and reasoning in a structured format</small>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Timeout (seconds)</label>
                <input
                  type="number"
                  value={agent.timeout || 300}
                  onChange={(e) => setAgent({ ...agent, timeout: parseInt(e.target.value) })}
                  min="1"
                  max="600"
                />
              </div>

              <div className="form-group">
                <label>Retry Count</label>
                <input
                  type="number"
                  value={agent.retry_count || 3}
                  onChange={(e) => setAgent({ ...agent, retry_count: parseInt(e.target.value) })}
                  min="0"
                  max="10"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
